'use client';

import { Fragment, useEffect, useState, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import {
  MapPin, CheckCircle,
  Search, Compass, ChevronDown,
  Home, ChevronRight as ChevronRightIcon, Music, X,
  Navigation
} from 'lucide-react';
import { motion } from 'framer-motion';
import EditorialHeader from '@/components/shared/EditorialHeader';
import EditorialFooter from '@/components/shared/EditorialFooter';
import ClaimSearchModal from '@/components/landing/ClaimSearchModal';
import DirectoryEditorialBanner from '@/components/directory/DirectoryEditorialBanner';
import { bricolageFont, dmSansFont } from '@/lib/localFonts';
import './directory-editorial.css';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LANDING_COLORS } from '@/constants/landing-colors';
import { Button } from '@/components/ui/button';
import { DirectoryFiltersBar, DirectoryListingCard, DirectorySearchSheet, DirectorySearchDropdown } from '@/components/directory';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useGeolocation, getCityCoordinates } from '@/hooks/useGeolocation';
import { DIRECTORY_TYPES, TYPE_LABELS, type DirectoryType } from '@/constants/directoryTypes';

interface Listing {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  city: string;
  address: string;
  province: string;
  latitude?: number | null;
  longitude?: number | null;
  rating: number | null;
  review_count: number;
  is_claimed: boolean;
  is_premium: boolean;
  is_promoted?: boolean;
  is_verified?: boolean;
  promotion_order?: number;
  view_count: number;
  observations?: string | null;
  category?: string | null;
  services?: string[] | null;
  instruments?: string[];
  price_range?: string | null;
  price_source_url?: string | null;
  price_verified_at?: string | null;
  logo_url?: string | null;
  photos?: Array<{ url: string; alt?: string }> | null;
}

interface FilterState {
  types: string[];
  instruments: string[];
  minRating: number | null;
  claimed: boolean | null;
  sortBy: string;
  cities: string[];
}

type TypeCount = Record<DirectoryType, number>;

type ViewMode = 'grid' | 'list';


import { API_BASE } from '@/lib/env';

const DIRECTORY_API_BASE = process.env.NODE_ENV === 'development' ? '/api/local' : API_BASE;
import { trackDirectoryFilter, trackDirectoryMapOpen, trackDirectoryLoadMore, trackPlpViewed } from '@/lib/analytics';
import { resolveDirectoryCity, stripAccents } from '@/constants/directoryCities';

const colors = { ...LANDING_COLORS.jams, primary: 'var(--color-brand)' };
const LazyDirectoryMapMini = dynamic(() => import('@/components/directory/DirectoryMapMini'), {
  ssr: false,
  loading: () => <DirectoryMapLoader />,
});

function DirectoryMapLoader() {
  return (
    <div className="directory-map-loader" role="status" aria-live="polite">
      <div className="directory-map-loader-art" aria-hidden="true">
        <span className="directory-map-loader-road directory-map-loader-road-a" />
        <span className="directory-map-loader-road directory-map-loader-road-b" />
        <span className="directory-map-loader-road directory-map-loader-road-c" />
        <span className="directory-map-loader-pin directory-map-loader-pin-a"><Music size={13} /></span>
        <span className="directory-map-loader-pin directory-map-loader-pin-b"><MapPin size={13} /></span>
        <span className="directory-map-loader-pin directory-map-loader-pin-c"><Music size={13} /></span>
      </div>
      <div className="directory-map-loader-copy">
        <span className="directory-map-loader-spinner" aria-hidden="true" />
        <strong>Preparando el mapa…</strong>
        <small>Los resultados ya están disponibles.</small>
      </div>
    </div>
  );
}
const displayFont = bricolageFont;
const bodyFont = dmSansFont;

// Interfaz para ciudades del API
interface CityOption {
  city: string;
  count: number;
  province: string;
}

export default function DirectoryIndex({ initialCity }: { initialCity?: string } = {}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [claimOpen, setClaimOpen] = useState(false);
  const mapReturnFocus = useRef<HTMLElement | null>(null);
  const claimReturnFocus = useRef<HTMLElement | null>(null);
  const openClaim = () => { claimReturnFocus.current = document.activeElement as HTMLElement | null; setClaimOpen(true); };
  const [mapError, setMapError] = useState(false);
  const listRequest = useRef(0);
  const mapRequest = useRef(0);
  useEffect(() => {
    const root = document.getElementById('directory-editorial');
    const header = root?.querySelector('header');
    const controls = root?.querySelector('.directory-controls');
    if (!root || !header || !controls) return;
    const sync = () => {
      root.style.setProperty('--directory-header-height', `${header.getBoundingClientRect().height}px`);
      root.style.setProperty('--directory-controls-height', `${controls.getBoundingClientRect().height}px`);
    };
    const observer = new ResizeObserver(sync);
    observer.observe(header); observer.observe(controls); sync();
    return () => observer.disconnect();
  }, []);
  const [listings, setListings] = useState<Listing[]>([]);
  const orderedListings = useMemo(() => {
    const featured = listings.filter(l => l.is_promoted).sort((a,b) => (a.promotion_order ?? 0) - (b.promotion_order ?? 0)).slice(0,3);
    const ids = new Set(featured.map(l => l.id));
    return [...featured, ...listings.filter(l => !ids.has(l.id))];
  }, [listings]);
  const [allListingsForMap, setAllListingsForMap] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [listingError, setListingError] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapSheetOpen, setMapSheetOpen] = useState(false);
  const [mapEnabled, setMapEnabled] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  // Analytics: PLP visto (top del funnel lead-gen). Una sola vez, cuando cargan los primeros resultados.
  const plpTracked = useRef(false);
  useEffect(() => {
    if (!loading && !plpTracked.current) {
      plpTracked.current = true;
      trackPlpViewed({ results_count: total, city: initialCity });
    }
  }, [loading, total, initialCity]);

  // El mapa (Mapbox + hasta 1.000 puntos) no compite con el primer listado.
  // Se activa cuando los resultados ya están visibles y el navegador queda libre.
  useEffect(() => {
    if (mapEnabled || loading) return;
    const activate = () => setMapEnabled(true);
    if (mapSheetOpen) {
      activate();
      return;
    }
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(activate, { timeout: isDesktop ? 900 : 1400 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(activate, isDesktop ? 300 : 600);
    return () => window.clearTimeout(id);
  }, [isDesktop, loading, mapEnabled, mapSheetOpen]);
  // On mobile, always use list view for non-promoted listings
  const effectiveViewMode: ViewMode = isDesktop ? 'grid' : 'list';
  const [typeCounts, setTypeCounts] = useState<TypeCount>({
    school: 0, rehearsal: 0, store: 0, club: 0, hub: 0,
  });

  // City selector state
  const [availableCities, setAvailableCities] = useState<CityOption[]>([]);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const cityDropdownRef = useRef<HTMLDivElement>(null);
  const geoAppliedRef = useRef(false);

  // Sábana de búsqueda (search overlay tipo GetYourGuide)
  const [searchSheetOpen, setSearchSheetOpen] = useState(false);
  const [searchSheetInitial, setSearchSheetInitial] = useState('');

  // Geolocation hook
  const { city: detectedCity, latitude: geoLat, longitude: geoLng, isLoading: geoLoading } = useGeolocation();

  // Extract URL params FIRST (needed by useMemo below)
  const type = searchParams.get('type') || '';
  // initialCity (prop) pre-filtra la página de ciudad (/directorio/{ciudad}); ?city= tiene prioridad.
  const city = searchParams.get('city') || initialCity || '';
  const page = parseInt(searchParams.get('page') || '1');

  // Helper to update search params in Next.js (replaces setSearchParams)
  function updateSearchParams(newParams: Record<string, string>, options?: { replace?: boolean }) {
    const params = new URLSearchParams(newParams);
    const url = `${pathname}?${params.toString()}`;
    if (options?.replace) {
      router.replace(url);
    } else {
      router.push(url);
    }
  }

  // Helper to merge with existing params
  function mergeSearchParams(updates: Record<string, string | null>, options?: { replace?: boolean }) {
    const params = new URLSearchParams(searchParams.toString());
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    const url = `${pathname}?${params.toString()}`;
    if (options?.replace) {
      router.replace(url);
    } else {
      router.push(url);
    }
  }

  // Calculate custom center for map based on geolocation or selected city
  const mapCustomCenter = useMemo(() => {
    // If city is selected, try to get coordinates for that city
    if (city) {
      const coords = getCityCoordinates(city);
      if (coords) {
        return { latitude: coords.lat, longitude: coords.lng, zoom: 11 };
      }
    }
    // If we have geolocation data, use it
    if (geoLat && geoLng) {
      return { latitude: geoLat, longitude: geoLng, zoom: 10 };
    }
    // Default: no custom center (map will calculate from listings)
    return undefined;
  }, [city, geoLat, geoLng]);

  // Advanced filters state - all types selected by default to show everything
  const [filters, setFilters] = useState<FilterState>({
    types: type.split(',').filter(t => DIRECTORY_TYPES.includes(t as DirectoryType)).length ? type.split(',').filter(t => DIRECTORY_TYPES.includes(t as DirectoryType)) : [...DIRECTORY_TYPES],
    instruments: [],
    minRating: null,
    claimed: null,
    sortBy: 'relevance',
    cities: [],
  });
  const limit = 24; // 6 filas × 4 columnas por "página" de carga
  // "Ver más": cuántos bloques de `limit` se han cargado (carga incremental, no paginación)
  const [loadedPages, setLoadedPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // Active search query - only set when user submits the search form
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  useEffect(() => {
    // Synchronize filter controls when browser navigation changes the URL.
    const nextTypes = type.split(',').filter(t => DIRECTORY_TYPES.includes(t as DirectoryType));
    const normalizedTypes = nextTypes.length ? nextTypes : [...DIRECTORY_TYPES];
    // Keep the same state reference when the URL already matches. On first
    // render this avoids triggering the listings request a second time.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilters(prev => prev.types.join(',') === normalizedTypes.join(',')
      ? prev
      : { ...prev, types: normalizedTypes });
  }, [type]);

  // Fetch available cities on mount
  useEffect(() => {
    async function fetchCities() {
      try {
        const res = await fetch(`${DIRECTORY_API_BASE}/directory/cities`);
        const data = await res.json();
        setAvailableCities(data.cities || []);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    }
    fetchCities();
  }, []);

  // Auto-detect city from geolocation (only once, if no city in URL)
  useEffect(() => {
    if (!geoLoading && detectedCity && !city && !geoAppliedRef.current) {
      // Check if detected city exists in our available cities
      const matchingCity = availableCities.find(
        c => c.city.toLowerCase() === detectedCity.toLowerCase() ||
             c.province.toLowerCase() === detectedCity.toLowerCase()
      );

      if (matchingCity) {
        geoAppliedRef.current = true;
        mergeSearchParams({ city: matchingCity.city }, { replace: true });
      }
    }
  }, [geoLoading, detectedCity, city, availableCities]);

  // Close city dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target as Node)) {
        setCityDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // On mount, clear any stale 'q' param from URL to show all results by default
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery) {
      // Remove the q param from URL without triggering a search
      mergeSearchParams({ q: null }, { replace: true });
    }
  }, []); // Only on mount

  // Buscador del navbar (aparece al scrollear): escucha el CustomEvent y dispara la búsqueda,
  // reutilizando el mismo estado que el buscador del hero (searchQuery + activeSearchQuery).
  useEffect(() => {
    const onNavSearch = (e: Event) => {
      const q = (e as CustomEvent<string>).detail?.trim() ?? '';
      setSearchQuery(q);
      setActiveSearchQuery(q);
      const newParams: Record<string, string> = { page: '1' };
      if (type) newParams.type = type;
      if (city) newParams.city = city;
      updateSearchParams(newParams);
    };
    window.addEventListener('directory:search', onNavSearch);
    return () => window.removeEventListener('directory:search', onNavSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, city]);

  // Sábana de búsqueda (overlay tipo GetYourGuide): la abre el buscador del hero (onFocus)
  // y el del navbar (CustomEvent 'directory:openSearch', sin acoplar navbar↔página).
  // El panel emite selectCity/selectType por evento (funciona igual en hero y navbar).
  useEffect(() => {
    const onOpenSearch = (e: Event) => {
      setSearchSheetInitial((e as CustomEvent<string>).detail ?? '');
      setSearchSheetOpen(true);
    };
    const onSelectCity = (e: Event) => handleCityChange((e as CustomEvent<string>).detail ?? '');
    const onSelectType = (e: Event) => mergeSearchParams({ type: (e as CustomEvent<string>).detail || null, page: '1' });
    window.addEventListener('directory:openSearch', onOpenSearch);
    window.addEventListener('directory:selectCity', onSelectCity);
    window.addEventListener('directory:selectType', onSelectType);
    return () => {
      window.removeEventListener('directory:openSearch', onOpenSearch);
      window.removeEventListener('directory:selectCity', onSelectCity);
      window.removeEventListener('directory:selectType', onSelectType);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar filtros/ciudad/tipo/búsqueda → volver a la primera página (reemplaza la lista)
  useEffect(() => {
    // Reset pagination when the externally selected query changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadedPages(1);
    fetchListings(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, city, filters, activeSearchQuery]);

  // Envuelve setFilters para trackear cambios de tipo/rating sin tocar el hijo.
  function handleFilterChange(next: FilterState | ((prev: FilterState) => FilterState)) {
    const resolved = typeof next === 'function' ? next(filters) : next;
    if (JSON.stringify(filters.types) !== JSON.stringify(resolved.types)) {
      trackDirectoryFilter('type', resolved.types.join(',') || '(ninguno)');
      mergeSearchParams({ type: resolved.types.length && resolved.types.length < DIRECTORY_TYPES.length ? resolved.types.join(',') : null, page: '1' });
    }
    if (filters.minRating !== resolved.minRating) trackDirectoryFilter('rating', String(resolved.minRating ?? '(cualquiera)'));
    setFilters(resolved);
  }

  // Abre el mapa del directorio registrando el evento (engagement de proximidad)
  function openMap() {
    mapReturnFocus.current = document.activeElement as HTMLElement | null;
    trackDirectoryMapOpen(effectiveViewMode);
    setMapSheetOpen(true);
  }

  // "Ver más": carga el siguiente bloque y lo añade
  function handleLoadMore() {
    const next = loadedPages + 1;
    setLoadedPages(next);
    trackDirectoryLoadMore(listings.length);
    fetchListings(next, true);
  }

  // Fetch all listings for map when filters change (not page)
  useEffect(() => {
    if (!mapEnabled) return;
    fetchAllListingsForMap();
  }, [mapEnabled, type, city, filters, activeSearchQuery]);

  async function fetchListings(pageToLoad = 1, append = false) {
    const request = ++listRequest.current;
    setListingError(false);
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String((pageToLoad - 1) * limit),
      });

      // Apply search query only if user has actively searched
      if (activeSearchQuery) params.set('q', activeSearchQuery);

      // Apply type filter from sidebar
      // If no types selected OR every public type is selected, show all (no filter)
      // Otherwise, filter by selected types
      if (filters.types.length > 0 && filters.types.length < DIRECTORY_TYPES.length) {
        params.set('type', filters.types.join(','));
      }
      // When 0 or 4 types selected, don't add type param (show all)

      if (city) params.set('city', city);
      if (filters.cities && filters.cities.length > 0) params.set('cities', filters.cities.join(','));
      if (filters.claimed) params.set('claimed', 'true');
      if (filters.minRating) params.set('minRating', String(filters.minRating));
      if (filters.sortBy !== 'relevance') params.set('sortBy', filters.sortBy);
      if (filters.instruments.length > 0) params.set('instruments', filters.instruments.join(','));

      const res = await fetch(`${DIRECTORY_API_BASE}/directory?${params}`);

      if (!res.ok) {
        console.error('Directory API error:', res.status, res.statusText);
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();
      if (request !== listRequest.current) return;

      // Handle API error responses
      if (data.error) {
        console.error('Directory API returned error:', data.error);
        throw new Error(data.error);
      }

      setListings(prev => append ? [...prev, ...(data.listings || [])] : (data.listings || []));
      setTotal(data.total || 0);

      // Update type counts if available from API
      if (data.typeCounts) {
        setTypeCounts(data.typeCounts);
      } else {
        // Calculate from current listings as fallback
        const counts = (data.listings || []).reduce((acc: TypeCount, l: Listing) => {
          if (l.type in acc) {
            acc[l.type as keyof TypeCount]++;
          }
          return acc;
        }, { school: 0, rehearsal: 0, store: 0, club: 0, hub: 0 });
        setTypeCounts(counts);
      }
    } catch (error) {
      if (request !== listRequest.current) return;
      console.error('Error fetching listings:', error);
      setListingError(true);
      if (!append) { setListings([]); setTotal(0); }
    }
    setLoading(false);
    setLoadingMore(false);
  }

  // Fetch ALL listings (no pagination) for the map
  async function fetchAllListingsForMap() {
    const request = ++mapRequest.current;
    setMapError(false);
    setAllListingsForMap([]);
    try {
      const params = new URLSearchParams({
        limit: '1000', // High limit to get all listings
        offset: '0',
        mapOnly: 'true', // Signal to API to only return fields needed for map
      });

      // Apply search query only if user has actively searched
      if (activeSearchQuery) params.set('q', activeSearchQuery);

      if (filters.types.length > 0 && filters.types.length < DIRECTORY_TYPES.length) {
        params.set('type', filters.types.join(','));
      }
      if (city) params.set('city', city);
      if (filters.cities && filters.cities.length > 0) params.set('cities', filters.cities.join(','));
      if (filters.claimed) params.set('claimed', 'true');
      if (filters.minRating) params.set('minRating', String(filters.minRating));
      if (filters.instruments.length > 0) params.set('instruments', filters.instruments.join(','));

      const res = await fetch(`${DIRECTORY_API_BASE}/directory?${params}`);
      if (!res.ok) throw new Error('Map request failed');
      const data = await res.json();
      if (data.error) throw new Error('Map response failed');
      if (request !== mapRequest.current) return;

      setAllListingsForMap(data.listings || []);
    } catch (error) {
      if (request !== mapRequest.current) return;
      console.error('Error fetching map listings:', error);
      // Fallback to current page listings
      setAllListingsForMap([]);
      setMapError(true);
    }
  }

  function handleClearSearch() {
    setSearchQuery('');
    setActiveSearchQuery('');
    const newParams: Record<string, string> = {};
    if (type) newParams.type = type;
    if (city) newParams.city = city;
    updateSearchParams(newParams);
  }

  function handleCityChange(newCity: string) {
    setCityDropdownOpen(false);
    setCitySearchQuery('');
    trackDirectoryFilter('city', newCity || '(todas)');
    // Si la ciudad tiene landing pSEO propia, navegar a ella (una intención = una URL indexable,
    // evita el duplicado /directorio?city=X vs /directorio/x y unifica la UX).
    if (newCity) {
      const plp = resolveDirectoryCity(newCity);
      if (plp) {
        router.push(`/directorio/${plp.slug}`);
        return;
      }
    }
    mergeSearchParams({
      city: newCity || null,
      page: '1',
    });
  }

  // Filter cities by search query (tolerante a acentos: "cordoba" ≈ "Córdoba")
  const filteredCities = useMemo(() => {
    if (!citySearchQuery) return availableCities.slice(0, 10);
    const query = stripAccents(citySearchQuery);
    return availableCities
      .filter(c =>
        stripAccents(c.city).includes(query) ||
        stripAccents(c.province).includes(query)
      )
      .slice(0, 10);
  }, [availableCities, citySearchQuery]);

  // Type label for FAQs (plural form)
  const typeLabelPlural = type ? (TYPE_LABELS[type]?.toLowerCase() + 's') : 'establecimientos de música';
  const locationText = city || 'España';

  // FAQ Schema for rich snippets - dynamic based on filters
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `¿Cuántas ${typeLabelPlural} hay en ${locationText}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Actualmente hay ${total} ${typeLabelPlural} registrados en ${locationText} en el directorio de Jamsom Music. Este número se actualiza constantemente a medida que más establecimientos se unen a nuestra plataforma.`,
        },
      },
      {
        '@type': 'Question',
        name: `¿Cómo puedo encontrar ${typeLabelPlural} cerca de mí?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Usa los filtros por tipo de establecimiento (escuelas, locales de ensayo, tiendas, clubes) y ciudad para encontrar ${typeLabelPlural} cercanos. Cada perfil incluye dirección completa, información de contacto y cómo llegar.`,
        },
      },
      {
        '@type': 'Question',
        name: `¿Es gratis consultar el directorio de ${typeLabelPlural}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, el directorio de Jamsom Music es completamente gratuito para consultar. Puedes ver información de contacto, ubicación, horarios y reseñas de cualquier establecimiento sin coste alguno.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Cómo puedo añadir mi negocio al directorio?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Si tienes una escuela de música, local de ensayo, tienda o club de música, puedes reclamar tu perfil gratuitamente haciendo clic en "¿Es tu negocio?" en la página de tu establecimiento. Verificaremos tu identidad y tendrás acceso a un panel de gestión.',
        },
      },
    ],
  };

  const inlineBusinessKind = filters.types.length === 1 && filters.types[0] === 'school' ? 'school' : 'rehearsal';
  const hasInlineBusiness = !loading && orderedListings.length >= 18;
  const hasInlineClaim = !loading && orderedListings.length >= 30;

  return (
    <div id="directory-editorial" className={`${displayFont.variable} ${bodyFont.variable}`}>
      {/* Structured data via script tags (replaces Helmet).
          En la página de ciudad (/directorio/{ciudad}) el JSON-LD ya lo emite la ruta server
          (ItemList + FAQPage por ciudad) → aquí lo omitimos para no duplicar el FAQPage. */}
      {!initialCity && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <EditorialHeader variant="directorio" ctaLabel="Añadir mi negocio" onCtaClick={openClaim} />
      <ClaimSearchModal returnFocusRef={claimReturnFocus} open={claimOpen} onOpenChange={setClaimOpen} colors={LANDING_COLORS.jams} />

      <div className="directory-body">
        {/* Hero Section — compacto: en ciudad es 1 fila (título izq + buscador/selector dcha) */}
        <section id="directory-search" className="directory-intro">


          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Hero: en /directorio (sin ciudad) grande centrado con badge+subtítulo;
                en página de ciudad compacto en 1 fila (título izq + buscador dcha). */}
            <motion.div
              className="directory-intro-inner"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Badge + subtítulo solo en el índice general (sin ciudad) */}
              {!city && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-4"
                  style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
                >
                  <Compass className="w-3.5 h-3.5" />
                  Directorio Musical
                </span>
              )}

              <h1 className={`font-bold text-gray-900 font-landing tracking-tight ${city ? 'text-lg md:text-2xl mb-0 flex-shrink-0' : 'text-2xl md:text-3xl lg:text-4xl mb-3'}`}>
                {type ? (
                  <>
                    {TYPE_LABELS[type] || 'Espacios musicales'} en{' '}
                    <span style={{ color: colors.primary }}>{city || 'España'}</span>
                  </>
                ) : city ? (
                  <>
                    Directorio musical en{' '}
                    <span style={{ color: colors.primary }}>{city}</span>
                  </>
                ) : (
                  <>
                    Encuentra tu próximo espacio{' '}
                    <span style={{ color: colors.primary }}>musical</span>
                  </>
                )}
              </h1>

              {!city && (
                <p className="text-gray-600 mb-6">
                  Escuelas, locales de ensayo, tiendas y clubes de música en España
                </p>
              )}

              {/* Search Bar + City Selector */}
              <div className={city ? 'w-full md:flex-1 md:max-w-2xl md:mx-auto' : 'max-w-2xl mx-auto'}>
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search Input — abre la sábana. Desktop: dropdown anclado bajo el input (GYG).
                      Móvil: bottom sheet (montado abajo, se abre con el mismo estado). */}
                  <form
                    data-search-box
                    onSubmit={(e) => { e.preventDefault(); setSearchSheetInitial(searchQuery); setSearchSheetOpen(true); }}
                    className="relative flex-1"
                  >
                    <div className="relative bg-white rounded-xl shadow-sm border border-gray-200">
                      <div className="flex items-center">
                        <Search className="absolute left-4 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Busca por ciudad o nombre..."
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); if (!searchSheetOpen) setSearchSheetOpen(true); }}
                          onFocus={() => setSearchSheetOpen(true)}
                          className="w-full pl-12 pr-24 py-3.5 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-sm rounded-xl"
                        />
                        <Button
                          type="submit"
                          size="sm"
                          className="absolute right-2 rounded-lg h-9 px-4 font-medium"
                          style={{ backgroundColor: colors.primary }}
                        >
                          Buscar
                        </Button>
                      </div>
                    </div>

                    {/* Desktop: dropdown anclado bajo el input. El input de arriba ES el buscador
                        (query compartido); el dropdown solo muestra resultados, sin barra propia. */}
                    {isDesktop && (
                      <DirectorySearchDropdown
                        open={searchSheetOpen}
                        onClose={() => setSearchSheetOpen(false)}
                        query={searchQuery}
                        cities={availableCities}
                        typeCounts={typeCounts}
                      />
                    )}
                  </form>

                  {/* City Selector Dropdown */}
                  <div className="relative" ref={cityDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
                      className="w-full sm:w-[200px] flex items-center justify-between gap-2 px-4 py-3.5 bg-white rounded-xl shadow-sm border border-gray-200 text-sm hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {geoLoading ? (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        ) : city ? (
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <Navigation className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span className={`truncate ${city ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                          {city || 'Toda España'}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${cityDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {cityDropdownOpen && (
                      <div className="absolute top-full right-0 sm:left-0 sm:right-auto mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-[200] overflow-hidden min-w-[280px] max-w-sm">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Buscar ciudad..."
                              value={citySearchQuery}
                              onChange={(e) => setCitySearchQuery(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Options */}
                        <div className="max-h-[280px] overflow-y-auto">
                          {/* "All Spain" option */}
                          <button
                            type="button"
                            onClick={() => handleCityChange('')}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                              !city ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <Navigation className="w-4 h-4" />
                              Toda España
                            </span>
                            {!city && <CheckCircle className="w-4 h-4 text-indigo-600" />}
                          </button>

                          {/* Detected city highlight */}
                          {detectedCity && !city && !citySearchQuery && (
                            <div className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 border-t border-b border-gray-100">
                              Cerca de ti
                            </div>
                          )}

                          {/* City list */}
                          {filteredCities.map((c) => (
                            <button
                              key={c.city}
                              type="button"
                              onClick={() => handleCityChange(c.city)}
                              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                                city === c.city ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">{c.city}</span>
                                <span className="text-gray-400 text-xs">{c.province}</span>
                              </span>
                              <span className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{c.count}</span>
                                {city === c.city && <CheckCircle className="w-4 h-4 text-indigo-600" />}
                              </span>
                            </button>
                          ))}

                          {filteredCities.length === 0 && citySearchQuery && (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">
                              No se encontraron ciudades
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Active search indicator */}
              {activeSearchQuery && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-600">
                    Buscando: <span className="font-medium">&quot;{activeSearchQuery}&quot;</span>
                  </span>
                  <button
                    onClick={handleClearSearch}
                    className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    <X className="w-3.5 h-3.5" />
                    Limpiar
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Subheader STICKY (fila 1: breadcrumb · fila 2: filtros) — se pega bajo el navbar (72px) al scrollear */}
        <div className="directory-controls">
          {/* Fila 1: breadcrumbs */}
          <div className="border-b border-gray-100">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <nav className="flex items-center gap-1.5 py-2 text-sm">
                <Link href="/" className="text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1">
                  <Home className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Inicio</span>
                </Link>
                <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300" />
                <Link href="/directorio" className="text-gray-500 hover:text-gray-700 transition-colors">
                  Directorio
                </Link>
                {type && (
                  <>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300" />
                    <span className="text-gray-900 font-medium">{TYPE_LABELS[type]}</span>
                  </>
                )}
                {city && (
                  <>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300" />
                    <span className="text-gray-900 font-medium">{city}</span>
                  </>
                )}
              </nav>
            </div>
          </div>

          {/* Fila 2: filtros (chips tipo/rating + Ordenar + Filtros) */}
          <DirectoryFiltersBar
            editorial
            filters={filters}
            onFilterChange={handleFilterChange}
            totalResults={total}
            typeCounts={typeCounts}
            availableCities={availableCities}
          />
        </div>

        {/* Responsive Map Modal - Sheet on mobile, Dialog on desktop */}
        <Dialog open={mapSheetOpen} onOpenChange={setMapSheetOpen}>
          <DialogContent className="directory-expanded-map" style={{width:'calc(100vw - 32px)',maxWidth:1400,height:'calc(100dvh - 32px)',display:'flex',flexDirection:'column'}} onCloseAutoFocus={e => { e.preventDefault(); mapReturnFocus.current?.focus(); }}>
            <DialogTitle>Explora los espacios en el mapa</DialogTitle>
            <DialogDescription>Selecciona un establecimiento para consultar su ficha.</DialogDescription>
            <div style={{flex:1,minHeight:0,overflow:'hidden',borderRadius:12}}>
              {mapEnabled ? <LazyDirectoryMapMini listings={allListingsForMap} customCenter={mapCustomCenter} selectedCity={city} onExpand={() => setMapSheetOpen(false)} persistent expanded /> : <DirectoryMapLoader />}
            </div>
          </DialogContent>
        </Dialog>

        {/* Sábana de búsqueda MÓVIL (bottom sheet). En desktop se usa el dropdown anclado al hero. */}
        {!isDesktop && (
          <DirectorySearchSheet
            open={searchSheetOpen}
            onOpenChange={setSearchSheetOpen}
            initialQuery={searchSheetInitial}
            cities={availableCities}
            typeCounts={typeCounts}
          />
        )}

        {/* Main Content */}
        <section className="directory-results-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="directory-workspace">
              <aside className="directory-map-pane" aria-label="Mapa de establecimientos"><div className="directory-map-sticky">
                {mapError ? <div className="directory-map-error" role="status"><MapPin size={24}/><p>No pudimos cargar los lugares del mapa.</p><button onClick={fetchAllListingsForMap}>Reintentar mapa</button></div> : mapEnabled ? <LazyDirectoryMapMini listings={allListingsForMap} selectedCity={city} customCenter={mapCustomCenter} onExpand={openMap} persistent /> : <DirectoryMapLoader />}
              </div></aside>
              {/* Main Content Area */}
              <div className="directory-listings">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-5 bg-white rounded-xl px-4 py-3 border border-gray-100">
                  <div className="flex items-center gap-3">
                    {/* El botón "Filtros" ya lo provee DirectoryFiltersBar (barra sticky) en mobile. */}
                    <span className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">{total.toLocaleString()}</span>
                      <span className="hidden sm:inline"> resultados</span>
                    </span>
                  </div>

                </div>

                {listingError && <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <p>No pudimos cargar los resultados. Comprueba tu conexión e inténtalo de nuevo.</p>
                  <button type="button" className="mt-2 min-h-11 font-semibold underline" onClick={() => fetchListings(1, false)}>Reintentar</button>
                </div>}
                {/* Grid View */}
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-white rounded-xl p-5 animate-pulse border border-gray-100"
                      >
                        <div className="flex items-start gap-4 mb-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                          <div className="flex-1">
                            <div className="h-5 bg-gray-200 rounded-lg mb-2 w-3/4" />
                            <div className="h-4 bg-gray-100 rounded-lg w-1/2" />
                          </div>
                        </div>
                        <div className="h-4 bg-gray-100 rounded-lg w-2/3" />
                      </div>
                    ))}
                  </div>
                ) : listingError && listings.length === 0 ? null : listings.length === 0 ? (
                  <motion.div
                    className="text-center py-16 bg-white rounded-2xl border border-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: `${colors.primary}10` }}
                    >
                      <Music className="w-8 h-8" style={{ color: colors.primary }} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No hay resultados</h3>
                    <p className="text-gray-500 max-w-md mx-auto mb-5 text-sm">
                      No encontramos establecimientos con esos filtros. Prueba con otros términos de búsqueda.
                    </p>
                    <Button
                      onClick={() => {
                        updateSearchParams({});
                        setFilters({ types: [...DIRECTORY_TYPES], instruments: [], minRating: null, claimed: null, sortBy: 'relevance', cities: [] });
                      }}
                      variant="outline"
                      className="rounded-lg"
                      style={{ borderColor: colors.primary, color: colors.primary }}
                    >
                      Ver todos los establecimientos
                    </Button>
                  </motion.div>
                ) : (
                  <div data-testid="directory-results-grid" className="directory-grid">
                    {orderedListings.map((listing, index) => <Fragment key={listing.id}>
                      <DirectoryListingCard listing={listing} layout={effectiveViewMode} position={index + 1} />
                      {index === Math.min(5, listings.length - 1) && <div className="directory-inline-banner"><DirectoryEditorialBanner kind="beta" /></div>}
                      {index === 17 && <div className="directory-inline-banner"><DirectoryEditorialBanner kind={inlineBusinessKind} /></div>}
                      {index === 29 && <div className="directory-inline-banner"><DirectoryEditorialBanner kind="claim" onClaim={openClaim} /></div>}
                    </Fragment>)}
                  </div>
                )}

                {/* "Ver más" — carga incremental estilo GetYourGuide (no paginación numérica) */}
                {!loading && listings.length < total && (
                  <div className="flex flex-col items-center gap-3 mt-8 py-6">
                    <p className="text-sm text-gray-500">
                      Mostrando {listings.length} de {total}
                    </p>
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-all hover:border-gray-400 hover:shadow-sm disabled:opacity-60"
                    >
                      {loadingMore ? 'Cargando…' : 'Ver más'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section id="directory-business" className="directory-business">
          <div className="directory-business-grid">
            {(filters.types.length !== 1 || filters.types[0] !== 'rehearsal') && !(hasInlineBusiness && inlineBusinessKind === 'school') && <DirectoryEditorialBanner kind="school" />}
            {(filters.types.length !== 1 || filters.types[0] !== 'school') && !(hasInlineBusiness && inlineBusinessKind === 'rehearsal') && <DirectoryEditorialBanner kind="rehearsal" />}
          </div>
          {!hasInlineClaim && <DirectoryEditorialBanner kind="claim" onClaim={openClaim} />}
        </section>
      </div>
      <EditorialFooter variant="directorio" onAction={openClaim} actionLabel="Añadir mi negocio" />
    </div>
  );
}
